/**
 * NotificationItem Component
 *
 * Single notification item displayed in the dropdown.
 * Shows:
 * - Icon (based on notification type)
 * - Title (bold if unread)
 * - Body (truncated to 100 chars)
 * - Relative time (e.g., "5m ago")
 * - Visual indicator for unread (light blue background)
 */

'use client';

import { cn } from '@/src/shared/lib/utils';
import { getNotificationIcon, getPriorityColors } from '../lib/notification-icons';
import { formatNotificationDate, truncateText } from '../lib/notification-utils';
import type { NotificationItem as NotificationItemType } from '../types';

interface NotificationItemProps {
  notification: NotificationItemType;
  locale: string;
  onClick: (notification: NotificationItemType) => void;
}

export function NotificationItem({ notification, locale, onClick }: NotificationItemProps) {
  const Icon = getNotificationIcon(notification.type);
  const priorityColors = getPriorityColors(notification.priority);

  return (
    <button
      onClick={() => onClick(notification)}
      className={cn(
        'w-full px-4 py-3 text-left transition-colors',
        'flex items-start gap-3 hover:bg-background-2',
        'border-b border-border-primary last:border-b-0',
        !notification.isRead && 'bg-blue-50'
      )}
      type="button"
    >
      {/* Icon with priority color background */}
      <div
        className={cn(
          'flex-shrink-0 p-2 rounded-full',
          priorityColors.bg,
          priorityColors.border,
          'border'
        )}
      >
        <Icon className={cn('h-5 w-5', priorityColors.icon)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Title */}
        <p
          className={cn(
            'text-sm text-text-primary',
            !notification.isRead && 'font-semibold'
          )}
        >
          {notification.title}
        </p>

        {/* Body (truncated) */}
        <p className="text-xs text-text-secondary mt-1 line-clamp-2">
          {truncateText(notification.body, 100)}
        </p>

        {/* Time */}
        <p className="text-xs text-text-tertiary mt-1">
          {formatNotificationDate(notification.createdAt, locale)}
        </p>
      </div>

      {/* Unread indicator dot */}
      {!notification.isRead && (
        <div className="flex-shrink-0 mt-1">
          <div className="h-2 w-2 bg-blue-600 rounded-full" />
        </div>
      )}
    </button>
  );
}
